import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';

const ProgramDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [program, setProgram] = useState(null);
    const [intakes, setIntakes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                // Load Program with University
                const { data: prog, error: progError } = await supabase
                    .from('programs')
                    .select('*, universities(name, country)')
                    .eq('id', id)
                    .single();

                if (progError) throw progError;
                setProgram(prog);

                // Load Intakes for this program
                const { data: ints, error: intError } = await supabase
                    .from('intakes')
                    .select('*')
                    .eq('program_id', id)
                    .order('start_date', { ascending: true });

                if (intError) throw intError;
                setIntakes(ints);

            } catch (e) {
                console.error(e);
                setError('Program not found');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id]);

    if (loading) return (
        <div className="min-h-screen bg-gray-900 font-mono text-white flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
            <div>Loading program details...</div>
        </div>
    );

    if (error || !program) return (
        <div className="min-h-screen bg-gray-900 font-mono text-white p-6">
            <div className="text-red-400 mb-4">{error || 'Program not found'}</div>
            <button onClick={() => navigate('/admin/universities')} className="text-blue-400 hover:underline">
                &larr; Back to Catalog
            </button>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-900 font-mono text-white p-6">
            <div className="max-w-4xl mx-auto">
                <button onClick={() => navigate('/admin/universities')} className="mb-4 text-gray-400 hover:text-white flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                    Back to Catalog
                </button>

                {/* Program Header */}
                <div className="bg-gray-800 border border-gray-700 rounded-lg shadow p-6 mb-6">
                    <div className="flex justify-between items-start">
                        <div>
                            <h1 className="text-3xl font-bold text-white mb-2">{program.name}</h1>
                            <div className="text-xl text-blue-400 mb-1">{program.universities?.name}</div>
                            <div className="text-gray-400 text-sm">{program.universities?.country}</div>
                        </div>
                        <div className="text-right">
                            <span className="inline-block bg-blue-900/50 text-blue-300 px-3 py-1 rounded-full text-sm font-medium border border-blue-800">
                                {program.level}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    {/* General Info */}
                    <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                        <h3 className="text-lg font-bold text-white mb-4 border-b border-gray-700 pb-2">General Information</h3>
                        <div className="space-y-3 text-sm">
                            <div className="flex justify-between">
                                <span className="text-gray-400">Discipline</span>
                                <span className="text-white">{program.discipline || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Course of Study</span>
                                <span className="text-white">{program.course_of_study || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Semesters</span>
                                <span className="text-white">{program.semesters || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Tuition Fee</span>
                                <span className="text-green-400 font-medium">
                                    {program.tuition ? `${program.tuition} ${program.currency || 'EUR'}` : '-'}
                                    {program.tuition_fee_type && <span className="text-gray-500 text-xs ml-1">({program.tuition_fee_type})</span>}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Application Method</span>
                                <span className="text-white">{program.application_method || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">App Opening</span>
                                <span className="text-white">{program.application_opening || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">App Deadline</span>
                                <span className="text-white">{program.application_deadline || '-'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Requirements */}
                    <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                        <h3 className="text-lg font-bold text-white mb-4 border-b border-gray-700 pb-2">Requirements</h3>
                        <div className="space-y-3 text-sm">
                            <div className="flex justify-between">
                                <span className="text-gray-400">Min A/L Passes</span>
                                <span className="text-white">{program.min_al_passes || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Min IELTS</span>
                                <span className="text-white">{program.min_ielts || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Min GPA</span>
                                <span className="text-white">{program.min_gpa || '-'}</span>
                            </div>
                            <div className="pt-2 mt-2 border-t border-gray-700">
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-400">Aptitude Test</span>
                                    <span className={`px-2 py-0.5 rounded text-xs ${program.has_aptitude_test ? 'bg-yellow-900/50 text-yellow-400 border border-yellow-800' : 'bg-gray-700 text-gray-400'}`}>
                                        {program.has_aptitude_test ? 'Required' : 'Not Required'}
                                    </span>
                                </div>
                                {program.has_aptitude_test && (
                                    <div className="mt-2 pl-2 border-l-2 border-yellow-800/50">
                                        <div className="flex justify-between mb-1">
                                            <span className="text-gray-500">Submission</span>
                                            <span className="text-gray-300">{program.aptitude_test_submission || '-'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">Deadline</span>
                                            <span className="text-gray-300">{program.aptitude_test_deadline || '-'}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Intakes */}
                <div>
                    <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                        <span className="mr-2">📅</span> Program Intakes
                    </h2>
                    <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-gray-900/50">
                                <tr>
                                    <th className="p-3 border-b border-gray-700 font-medium text-gray-400">Term</th>
                                    <th className="p-3 border-b border-gray-700 font-medium text-gray-400">Apply Open</th>
                                    <th className="p-3 border-b border-gray-700 font-medium text-gray-400">Apply Close</th>
                                    <th className="p-3 border-b border-gray-700 font-medium text-gray-400">Start Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {intakes.map(i => (
                                    <tr key={i.id} className="hover:bg-gray-700/50">
                                        <td className="p-3 border-b border-gray-700 font-medium">{i.term}</td>
                                        <td className="p-3 border-b border-gray-700">{i.apply_open || '-'}</td>
                                        <td className="p-3 border-b border-gray-700 text-yellow-400">{i.apply_close || '-'}</td>
                                        <td className="p-3 border-b border-gray-700">{i.start_date || '-'}</td>
                                    </tr>
                                ))}
                                {intakes.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="p-8 text-center text-gray-500">No intakes found for this program.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default ProgramDetailPage;
